/**
 * Custom hook for managing bill filter state and logic
 * Extracted from LegislationPage.tsx for better maintainability and reusability
 */

import { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { toLocalDateString } from '../utils/dateUtils';
import { parseLegislationSearchParams } from '../utils/legislationUrlState';

/*
  Multi-select filters are arrays; single-select ones stay strings.

  An empty array means "no filter" — there is no 'All subjects' sentinel in
  state any more. The API still accepts that sentinel so bookmarked URLs and
  saved filters keep working, but nothing in the app produces it.
*/
export interface BillFilters {
  chamber: string;
  subject: string[];
  status: string[];
  sponsor: string[];
  /** Stable `committees.id`s for API/URL (preferred over the `committee` string). */
  committee_id: string[];
  /** Legacy `house:name` or name-only filter; kept for old URLs/saved filters until migrated. */
  committee: string;
  legislator_id: string;
  bill_status: string;
  includeIntroduced: boolean;
  includeAmended: boolean;
  startDate: string;
  endDate: string;
  bipartisan: boolean;
  /** Only bills marked hot in a workspace the user can access. */
  hot: boolean;
  lobbyist_id: string;
  lobbyist_name: string;
  lobbyist_status: string;
  client_id: string;
  client_name: string;
  trackingStatus: string;
  session: string[];
}

export interface FilterOptions {
  subjects: string[];
  sponsors: string[];
  committees: Committee[];
}

export interface Committee {
  id: string | number;
  name: string;
  chamber: string;
}

/**
 * Coerce a persisted filter value into the multi-select array shape.
 *
 * Filters saved before multi-select stored plain strings ('Education', '', '12',
 * 'All subjects'). Those rows still exist, and an unchecked cast would hand a
 * string to code that calls `.filter`/`.map` on it — a TypeError, not a type
 * error, so the compiler cannot catch it. Run saved filters through this.
 */
export function toFilterArray(value: unknown, sentinels: string[] = []): string[] {
  const skip = new Set(sentinels.map((s) => s.toLowerCase()));
  const raw = Array.isArray(value) ? value : [value];
  const out: string[] = [];
  for (const item of raw) {
    if (item === undefined || item === null) continue;
    const trimmed = String(item).trim();
    if (!trimmed) continue;
    if (skip.has(trimmed.toLowerCase())) continue;
    if (!out.includes(trimmed)) out.push(trimmed);
  }
  return out;
}

/** Normalize legacy committee query strings so minor punctuation differences still match options. */
export function legacyCommitteeStringsEqual(a: string, b: string): boolean {
  if (a === b) return true;
  const norm = (s: string) =>
    s.trim().toLowerCase().replace(/\s*&\s*/g, ' and ').replace(/\s+/g, ' ');
  return norm(a) === norm(b);
}

/** Map a legacy `committee` param to a committee id once filter options are loaded. */
export function findCommitteeIdFromLegacy(
  legacy: string,
  committees: Committee[]
): string | null {
  if (!legacy) return null;
  for (const c of committees) {
    const filterValue = c.chamber
      ? `${String(c.chamber).toLowerCase()}:${c.name}`
      : c.name;
    if (legacyCommitteeStringsEqual(filterValue, legacy)) {
      return String(c.id);
    }
  }
  return null;
}

// Sentinel resolved server-side; never a wall-clock year (see useSessions.ts).
import { CURRENT_SESSION } from './useSessions';

const DEFAULT_FILTERS: BillFilters = {
  chamber: 'All',
  subject: [],
  status: [],
  sponsor: [],
  committee_id: [],
  committee: '',
  legislator_id: '',
  bill_status: '',
  includeIntroduced: false,
  includeAmended: false,
  startDate: '',
  endDate: '',
  bipartisan: false,
  hot: false,
  lobbyist_id: '',
  lobbyist_name: '',
  lobbyist_status: '',
  client_id: '',
  client_name: '',
  trackingStatus: '',
  session: [CURRENT_SESSION]
};

/**
 * Helper function to get default date range (7 days ago to today)
 */
export function getDefaultDateRange() {
  const today = new Date();
  const sevenDaysAgo = new Date(today);
  sevenDaysAgo.setDate(today.getDate() - 7);
  
  return {
    startDate: toLocalDateString(sevenDaysAgo),
    endDate: toLocalDateString(today)
  };
}

/**
 * Session counts as an active filter only when narrowed away from default (current year)
 * or explicitly set to all sessions via deep link (empty string = not a user filter).
 */
export function isSessionFilterActive(session: string[]): boolean {
  // Empty = all sessions via deep link, which is not a user-applied filter.
  if (session.length === 0) return false;
  return !(session.length === 1 && session[0] === CURRENT_SESSION);
}

/**
 * Check if any filters are currently active
 */
export function hasActiveFilters(filters: BillFilters, searchQuery: string, searchScope?: string): boolean {
  return (
    searchQuery !== '' ||
    (searchScope != null && searchScope !== 'metadata') ||
    filters.chamber !== 'All' ||
    filters.subject.length > 0 ||
    filters.status.length > 0 ||
    filters.sponsor.length > 0 ||
    filters.committee_id.length > 0 ||
    filters.committee !== '' ||
    filters.legislator_id !== '' ||
    filters.bill_status !== '' ||
    filters.bipartisan ||
    filters.hot ||
    filters.lobbyist_id !== '' ||
    (filters.lobbyist_status !== '' && filters.lobbyist_status !== 'all') ||
    filters.client_id !== '' ||
    filters.includeIntroduced ||
    filters.includeAmended ||
    filters.startDate !== '' ||
    filters.endDate !== '' ||
    isSessionFilterActive(filters.session) ||
    filters.trackingStatus !== ''
  );
}

/**
 * Filter committees based on selected chamber
 */
export function getFilteredCommittees(committees: Committee[], chamber: string): Committee[] {
  if (!committees.length) return [];
  
  if (chamber === 'All' || chamber === 'All chambers' || chamber === 'Resolutions') {
    return committees; // Show all committees
  } else if (chamber === 'House') {
    return committees.filter(c => c.chamber?.toLowerCase() === 'house');
  } else if (chamber === 'Senate') {
    return committees.filter(c => c.chamber?.toLowerCase() === 'senate');
  }
  
  return committees;
}

/**
 * Format committee name with chamber prefix for display
 */
export function formatCommitteeDisplayName(committee: Committee): string {
  const chamber = committee.chamber?.toLowerCase();
  if (chamber === 'house') {
    return `H-${committee.name}`;
  } else if (chamber === 'senate') {
    return `S-${committee.name}`;
  }
  return committee.name; // Fallback if no chamber info
}

/**
 * Custom hook for managing bill filters with URL synchronization
 */
export function useBillFilters() {
  const location = useLocation();
  
  const [filters, setFilters] = useState<BillFilters>(DEFAULT_FILTERS);
  const [filterOptions, setFilterOptions] = useState<FilterOptions>({
    subjects: [],
    sponsors: [],
    committees: []
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [urlParsingComplete, setUrlParsingComplete] = useState(false);
  const [currentLegislator, setCurrentLegislator] = useState<{id: string, name: string} | null>(null);

  /**
   * Parse URL parameters and initialize filters
   */
  const initializeFromUrl = useCallback(() => {
    const { filters: updatedFilters, searchQuery: urlSearchQuery } =
      parseLegislationSearchParams(location.search);

    setFilters(updatedFilters);

    if (urlSearchQuery) {
      setSearchQuery(urlSearchQuery);
    }

    setUrlParsingComplete(true);
  }, [location.search]);

  /**
   * Initialize filters from URL on mount
   */
  useEffect(() => {
    initializeFromUrl();
  }, [initializeFromUrl]);

  /**
   * Clear all filters and search
   */
  const clearFilters = useCallback(() => {
    setFilters(DEFAULT_FILTERS);
    setSearchQuery('');
  }, []);

  /**
   * Update a specific filter
   */
  const updateFilter = useCallback(<K extends keyof BillFilters>(key: K, value: BillFilters[K]) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  }, []);

  /**
   * Update multiple filters at once
   */
  const updateFilters = useCallback((updates: Partial<BillFilters>) => {
    setFilters(prev => ({ ...prev, ...updates }));
  }, []);

  return {
    filters,
    filterOptions,
    searchQuery,
    urlParsingComplete,
    currentLegislator,
    setFilters,
    setFilterOptions,
    setSearchQuery,
    setCurrentLegislator,
    clearFilters,
    updateFilter,
    updateFilters,
    hasActiveFilters: hasActiveFilters(filters, searchQuery),
    getFilteredCommittees: useCallback(
      () => getFilteredCommittees(filterOptions.committees, filters.chamber),
      [filterOptions.committees, filters.chamber]
    )
  };
}

